import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { collection, onSnapshot } from 'firebase/firestore';
import { EmailTemplate } from 'shared/types';
import { db } from 'src/firebase/firebaseConfig';
import { processTimestamps } from 'src/utils/appDateFunctions';

interface StaffGlobalState {
  emailTemplates: EmailTemplate[];
  isLoading: boolean;
  error: string | null;
}

// Track active listeners to avoid duplicates
let activeEmailTemplatesListener: (() => void) | null = null;

// Initial state
const initialState: StaffGlobalState = {
  emailTemplates: [],
  isLoading: false,
  error: null
};

// Setup email templates listener thunk
export const setupEmailTemplatesListener = createAsyncThunk(
  'staffGlobal/setupEmailTemplatesListener',
  async (_, { dispatch }) => {
    // Clean up existing listener first
    if (activeEmailTemplatesListener) {
      activeEmailTemplatesListener();
      activeEmailTemplatesListener = null;
    }
    dispatch(updateEmailTemplates([]));

    try {
      // Set up the listener for the email templates collection
      const emailTemplatesCollection = collection(db, 'templateEmails');

      activeEmailTemplatesListener = onSnapshot(
        emailTemplatesCollection,
        (querySnapshot) => {
          const templates: EmailTemplate[] = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const processedData = processTimestamps(data);
            templates.push({ id: doc.id, ...processedData } as EmailTemplate);
          });
          dispatch(updateEmailTemplates(templates));
        },
        (error) => {
          console.error('Error fetching email templates:', error);
          dispatch(setError(error.message));
        }
      );

      return true;
    } catch (error) {
      console.error('Error setting up email templates listener:', error);
      throw error;
    }
  }
);

// Clean up listener when no longer needed
export const cleanupEmailTemplatesListener = createAsyncThunk(
  'staffGlobal/cleanupEmailTemplatesListener',
  async (_, { dispatch }) => {
    if (activeEmailTemplatesListener) {
      activeEmailTemplatesListener();
      activeEmailTemplatesListener = null;

      // Always clear templates when cleaning up
      dispatch(updateEmailTemplates([]));
    }
    return null;
  }
);

// Create the staffGlobal slice
const staffGlobalSlice = createSlice({
  name: 'staffGlobal',
  initialState,
  reducers: {
    updateEmailTemplates: (state, action: PayloadAction<EmailTemplate[]>) => {
      state.emailTemplates = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Setup email templates listener cases
      .addCase(setupEmailTemplatesListener.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setupEmailTemplatesListener.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(setupEmailTemplatesListener.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to setup email templates listener';
      })
      // Cleanup email templates listener cases
      .addCase(cleanupEmailTemplatesListener.fulfilled, (state) => {
        state.isLoading = false;
      });
  }
});

export const { updateEmailTemplates, setError } = staffGlobalSlice.actions;

// Selectors
export const selectEmailTemplates = (state: { staffGlobal: StaffGlobalState }) => state.staffGlobal.emailTemplates;

export default staffGlobalSlice.reducer;
